import { useCallback, useEffect, useRef, useState } from 'react';

import { migrateCustomProfiles, type Persona } from '@workspace/personas';
import type { UserTaskProfile } from '@workspace/model-presets';
import { useAppStorage } from '@workspace/ui/hooks/useAppStorage';
import { useAuth } from '@workspace/ui/hooks/useAuth';

const PERSONAS_STORAGE_KEY = 'ai-assist-personas';

/** Shape persisted to localStorage + remote `chat/personas.json`. */
interface PersonasFile {
  /** User-created personas (source: "user"). Templates are static in-package. */
  personas: Persona[];
  /** Marks the one-time customProfiles → personas migration as done. */
  migratedFromProfiles?: boolean;
}

interface UsePersonasOptions {
  /**
   * Legacy custom task profiles to migrate on first load (from ChatSettings).
   * Each becomes a user persona pinned to its source model (or `fallbackModelId`).
   */
  legacyCustomProfiles?: UserTaskProfile[];
  /** Model to pin migrated personas to when a profile recorded no source model. */
  fallbackModelId: string;
}

/**
 * User personas store (tdd-0044). Mirrors useSettings: localStorage immediate +
 * debounced remote `chat/personas.json` when authenticated. Runs the one-time
 * customProfiles → personas migration when no personas file exists yet.
 *
 * Templates are NOT stored here — they are static in @workspace/personas and
 * never written to user storage.
 */
export function usePersonas({ legacyCustomProfiles, fallbackModelId }: UsePersonasOptions) {
  const { authenticated } = useAuth();
  const {
    data: remoteFile,
    loading: remoteLoading,
    save: saveRemote,
  } = useAppStorage<PersonasFile>('chat', 'personas.json');

  const [personas, setPersonas] = useState<Persona[]>([]);
  const [isHydrated, setIsHydrated] = useState(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Refs so the load effect can run migration without re-firing on prop changes.
  const legacyProfilesRef = useRef(legacyCustomProfiles);
  legacyProfilesRef.current = legacyCustomProfiles;
  const fallbackModelIdRef = useRef(fallbackModelId);
  fallbackModelIdRef.current = fallbackModelId;

  const persist = useCallback(
    (file: PersonasFile) => {
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem(PERSONAS_STORAGE_KEY, JSON.stringify(file));
        } catch { /* localStorage full */ }
      }
      if (authenticated) {
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(() => {
          saveRemote(file).catch((err) =>
            console.error('Failed to save personas to remote:', err),
          );
        }, 500);
      }
    },
    [authenticated, saveRemote],
  );

  // Load: prefer remote (authenticated), fall back to localStorage. If no file
  // exists anywhere, run the one-time migration from legacy custom profiles.
  useEffect(() => {
    if (remoteLoading) return;

    const readLocal = (): PersonasFile | null => {
      if (typeof window === 'undefined') return null;
      try {
        const stored = localStorage.getItem(PERSONAS_STORAGE_KEY);
        return stored ? (JSON.parse(stored) as PersonasFile) : null;
      } catch {
        return null;
      }
    };

    const existing =
      authenticated && remoteFile ? remoteFile : readLocal();

    if (existing) {
      setPersonas(existing.personas ?? []);
      setIsHydrated(true);
      return;
    }

    // No personas file yet → migrate legacy custom profiles (idempotent: only
    // happens when the file is absent). createdAt stamped at call site below.
    const legacy = legacyProfilesRef.current ?? [];
    const migrated = legacy.length
      ? migrateCustomProfiles(legacy, fallbackModelIdRef.current, Date.now())
      : [];
    const file: PersonasFile = { personas: migrated, migratedFromProfiles: true };
    setPersonas(migrated);
    if (migrated.length) persist(file);
    setIsHydrated(true);
  }, [authenticated, remoteFile, remoteLoading, persist]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  const savePersona = useCallback(
    (persona: Persona) => {
      setPersonas((prev) => {
        const idx = prev.findIndex((p) => p.id === persona.id);
        const next =
          idx >= 0
            ? prev.map((p) => (p.id === persona.id ? persona : p))
            : [...prev, persona];
        persist({ personas: next, migratedFromProfiles: true });
        return next;
      });
    },
    [persist],
  );

  const deletePersona = useCallback(
    (personaId: string) => {
      setPersonas((prev) => {
        const next = prev.filter((p) => p.id !== personaId);
        persist({ personas: next, migratedFromProfiles: true });
        return next;
      });
    },
    [persist],
  );

  return { personas, isHydrated, savePersona, deletePersona };
}
